import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Container,
  Chip,
  Tooltip,
  Skeleton,
  Alert,
  Slide,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import GroupIcon from "@mui/icons-material/Group";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SKELETON_ROWS = 6;

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    setLoading(true);
    axios
      .get("https://api.dilanaslan.com/api/users")
      .then((res) => {
        setUsers(res.data);
        setLoading(false);
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Kullanıcılar yüklenemedi",
          severity: "error",
        });
        setLoading(false);
      });
  };

  const handleRoleSelect = (user, role) => {
    setSelectedUser(user);
    setNewRole(role);
    setModalOpen(true);
  };

  const handleConfirmRoleChange = () => {
    axios
      .put(`https://api.dilanaslan.com/api/users/${selectedUser._id}/role`, {
        role: newRole,
      })
      .then(() => {
        setSnackbar({
          open: true,
          message: "Rol başarıyla güncellendi",
          severity: "success",
        });
        fetchUsers();
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Rol güncellenemedi",
          severity: "error",
        });
      })
      .finally(() => {
        setModalOpen(false);
        setSelectedUser(null);
        setNewRole("");
      });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setNewRole("");
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ py: { xs: 4, md: 5 }, position: "relative" }}
    >
      {/* Arka plan */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          background: (t) =>
            `radial-gradient(circle at 20% 18%, ${alpha(
              t.palette.primary.light,
              0.35
            )}, transparent 65%), linear-gradient(135deg, ${
              t.palette.background.default
            }, ${t.palette.background.paper})`,
        }}
      />

      {/* Header */}
      <Paper
        elevation={0}
        sx={(t) => ({
          mb: 4,
          p: 3,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          gap: 2,
          backdropFilter: "blur(18px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
        })}
      >
        <Box
          sx={(t) => ({
            width: 52,
            height: 52,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(t.palette.primary.main, 0.15),
            border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
            flexShrink: 0,
          })}
        >
          <GroupIcon color="primary" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Kullanıcılar
          </Typography>
          <Chip
            size="small"
            label={`${users.length} Kullanıcı`}
            sx={{
              mt: 1,
              fontWeight: 600,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          />
        </Box>
      </Paper>

      {/* Tablo */}
      <Paper
        elevation={0}
        sx={(t) => ({
          borderRadius: 4,
          backdropFilter: "blur(14px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
          overflow: "hidden",
        })}
      >
        <TableContainer sx={{ maxHeight: 620 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Kullanıcı Adı</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ad Soyad</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rol</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={120} height={28} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="40%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="50%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={80} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading &&
                users.map((user) => {
                  const isProtected = user.username === "semihkececioglu";
                  return (
                    <TableRow
                      key={user._id}
                      hover
                      sx={{
                        transition: ".25s",
                        "&:hover": {
                          backgroundColor: (t) =>
                            alpha(t.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                        onClick={() => navigate(`/profile/${user.username}`)}
                      >
                        <Avatar
                          src={user.profileImage}
                          alt={user.username}
                          sx={{ width: 36, height: 36 }}
                        />
                        {user.username}
                      </TableCell>
                      <TableCell>
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : "-"}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            isProtected
                              ? "Bu kullanıcının rolü değiştirilemez"
                              : "Rolü değiştir"
                          }
                          arrow
                        >
                          <span>
                            <Select
                              size="small"
                              value={user.role}
                              onChange={(e) =>
                                !isProtected &&
                                handleRoleSelect(user, e.target.value)
                              }
                              disabled={isProtected}
                              sx={{ minWidth: 120, borderRadius: 2 }}
                            >
                              <MenuItem value="user">User</MenuItem>
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="editor">Editör</MenuItem>
                            </Select>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Snackbar */}
      <Slide direction="up" in={snackbar.open} mountOnEnter unmountOnExit>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            borderRadius: 3,
            boxShadow: 3,
            minWidth: 300,
          }}
        >
          {snackbar.message}
        </Alert>
      </Slide>

      {/* Role Change Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        PaperProps={{
          sx: { borderRadius: 4, maxWidth: 400, width: "100%" },
        }}
      >
        <DialogTitle>Rol Değişikliği Onayı</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedUser?.username}</strong> kullanıcısının rolünü{" "}
            <strong>{newRole}</strong> yapmak istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Vazgeç</Button>
          <Button
            onClick={handleConfirmRoleChange}
            variant="contained"
            color="primary"
          >
            Evet, Değiştir
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsersPage;
